import { defineStore } from 'pinia';
import {
  EntityConfigurationErrors,
  getInitialEntityState,
} from '@/store/common/entities-state';
import { createEntityActions } from '@/store/common/entities-actions';
import { Collection } from '@/store/common/collections';
import { createEntityGetters } from '@/store/common/entities-getters';
import { Machine } from '@/model/machine';
import { InputOption } from '@/model/input-option';
import i18n from '@/config/i18n';
import { useMachineModelsStore } from '@/store/machine-models-store';
import { useVendorsStore } from '@/store/vendors-store';
import { reactive } from 'vue';

function sortByName(a: Machine, b: Machine): number {
  return a.name.localeCompare(b.name);
}

const initialEntityState = reactive({
  ...getInitialEntityState<Machine>(sortByName),
});

export const useMachinesStore = defineStore('machines', {
  state: () => initialEntityState,
  getters: {
    ...createEntityGetters<Machine>(),
    getForMachineModelId:
      (state) =>
      (machineModelId: number): Machine[] => {
        return state.entities
          .filter((machine) => machine.machine_model_id === machineModelId)
          .sort(sortByName);
      },
    getMachinesAsSelectOptions:
      (state) =>
      (
        locationId: number | null = null,
        categoryId: number | null = null,
      ): InputOption[] => {
        return state.entities
          .filter(
            (machine) =>
              (locationId ? machine.location_id === locationId : false) &&
              (categoryId ? machine.machine_model_id === categoryId : false),
          )
          .map((machine) => {
            return {
              label: machine.name,
              value: machine.id,
            };
          });
      },
  },
  actions: {
    ...createEntityActions<Machine>(Collection.MACHINES, initialEntityState),
    verifyConfiguration() {
      const machineModelsStore = useMachineModelsStore();
      const vendorsStore = useVendorsStore();
      if (!vendorsStore.currentEntity.machines_enabled) {
        this.setConfigurationErrors([]);
        return;
      }
      const machinesErrors: EntityConfigurationErrors[] = this.entities
        .filter(
          (machine: Machine) =>
            machineModelsStore.getById(machine.machine_model_id)?.services
              .length === 0,
        )
        .map((machine: Machine) => ({
          id: machine.id,
          errors: [i18n.global.t('configError.machineServices')],
        }));
      this.setConfigurationErrors(machinesErrors);
    },
  },
});
